import {
  describe,
  downloadGeoJSON,
  featureCollection,
  fillStyle,
  lineFeature,
  pointFeature,
  polygonFeature,
  strokeStyle,
} from '../../common/geojson';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:Mepbay
 */
const GEOJSON_MODULE = 'mepbay';

/**
 * @constant CONTROL_POINTS
 * @description The three points a parabolic bay model is built from, in the order the method uses
 * them, paired with the name each one carries in the export. They are stored under the names the
 * drawing flow gave them, which are not the names the method is known by.
 * @memberof module:Mepbay
 */
export const CONTROL_POINTS = [
  { key: 'diffractionPoint', name: 'diffraction_point', label: 'Diffraction point', order: 1 },
  { key: 'coastalControlPoint', name: 'closure_point', label: 'Closure point', order: 2 },
  { key: 'predominantWavePoint', name: 'tangent_point', label: 'Tangent point', order: 3 },
];

/**
 * @function layerFeatures
 * @description One bay's geometry: the fitted parabolic curve, the three control points it was
 * built from, and the line joining them.
 * The model's coefficients travel on the curve, since they describe it: a bay exported without
 * them is a shape nobody can check the fit of.
 * @param {Object} layer - A MEPBay layer holding `parabolicResults` and `controlPoints`.
 * @memberof module:Mepbay
 * @returns {Array<Object|null>} The layer's features.
 */
const layerFeatures = (layer) => {
  const results = layer?.parabolicResults;
  if (!results) return [];

  const shared = { bay: layer.name, bay_id: layer.id };
  const points = layer.controlPoints || {};

  const curve = lineFeature(
    (results.linePoints || []).map((point) => [point.lng, point.lat]),
    'parabolic_curve',
    {
      ...shared,
      ...describe(`Parabolic curve - ${layer.name}`, 'Fitted parabolic bay shape'),
      ...strokeStyle(layer.color || '#e53935', { width: 3 }),
      beta_angle: results.beta,
      cos_beta: results.cosBetaConf,
      C0: results.C0,
      C1: results.C1,
      C2: results.C2,
      R0: results.R0,
    },
  );

  const controlPoints = CONTROL_POINTS.map(({ key, name, label, order }) =>
    points[key]
      ? pointFeature([points[key].lng, points[key].lat], 'control_points', {
          ...shared,
          type: name,
          point_number: order,
          ...describe(`${label} - ${layer.name}`),
        })
      : null,
  );

  const controlLine = lineFeature(
    CONTROL_POINTS.map(({ key }) => points[key]).filter(Boolean).map((point) => [point.lng, point.lat]),
    'control_lines',
    { ...shared, ...describe(`Control line - ${layer.name}`), ...strokeStyle('#546e7a', { width: 2 }) },
  );

  return [curve, ...controlPoints, controlLine];
};

/**
 * @function buildGeoJsonExport
 * @description Gathers every drawn bay into one FeatureCollection, alongside the area of interest.
 * Bays that have not been fitted yet contribute nothing rather than an empty shell.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring, `[lng, lat]` pairs.
 * @param {Object} [params.layers] - MEPBay's layers, keyed by id; the ROI layer is skipped, since
 *  the area is written from `coordinates` instead.
 * **Called by:
 * @see {ProcessingPage} - MEPBay's result page, through the GIS export icon.
 * @memberof module:Mepbay
 * @returns {Object} A FeatureCollection, empty when no bay has been fitted yet.
 */
export const buildGeoJsonExport = ({ coordinates, layers }) =>
  featureCollection([
    polygonFeature(coordinates, 'area_of_interest', { ...describe('Area of interest'), ...fillStyle('#2b6fac', 0) }),
    Object.values(layers || {})
      .filter((layer) => !layer.isROI)
      .flatMap(layerFeatures),
  ]);

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {ProcessingPage} - MEPBay's result page.
 * @memberof module:Mepbay
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
